import {Component} from 'panel';
import queryString from 'query-string';

import {getCookie} from 'common/util/cookie';
import {compoundSentence} from 'common/util/string';
import {api, SignupOptions} from 'media/js/auth/api';
import {COMPANY_SIZE_OPTIONS, DEPARTMENT_OPTIONS, MAU_SIZE_OPTIONS} from 'media/js/auth/constants';
import {registerProperties, TrackProperties} from 'media/js/auth/tracking';
import {objToFormData} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {SignUpStep} from 'media/js/auth/signup/forms/auth-signup-form/constants';

import {FormErrors, FormValues} from '../types';
import {validationRules} from '../validation';
import {ErrorKey, FieldKey, ValidationState} from './constants';
import {AuthFormDefaultState, ErrorTypes} from './types';

export abstract class BaseRegistrationForm extends Component<AuthFormDefaultState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'already-invited-error': `json`,
      next: `string`,
      'is-gdpr': `boolean`,
      'promo-slug': `string`,
      'social-credential': `string`,
      'enable-google-sign-in': `boolean`,
    };
  }
  baseConfig() {
    return {
      defaultState: {
        error: null,
        formErrors: {},
        formValues: {
          name: ``,
          email: ``,
          password: ``,
          socialCredential: ``,
          company: ``,
          numberOfEmployees: null,
          numberOfMaus: null,
          agreedToTermsOfUse: false,
          agreedToEmailPromo: false,
          department: null,
        },
        notification: null,
        waitingForResponse: false,
      },
      helpers: {
        COMPANY_SIZE_OPTIONS,
        MAU_SIZE_OPTIONS,
        DEPARTMENT_OPTIONS,
        getSignInWithGoogleClientId: () => {
          // For some reason, the technique above for using the constants directly
          // doesn't work with webpack env specific globals like this one...
          return SIGN_IN_WITH_GOOGLE_CLIENT_ID;
        },
        getValidationStatus: (field) => this.getValidationStatus(field),
        handleContinue: (ev) => this.handleContinue?.(ev),
        handleBack: () => this.handleBack && this.handleBack(),
        handleSignup: (ev) => this.handleSignup(ev),
        handleFieldBlur: (field) => {
          this.validateFormFields([field]);
        },
        handleEmailPromoCheckboxClick: () => {
          this.updateFormValues({agreedToEmailPromo: !this.state.formValues.agreedToEmailPromo});
        },
        handleFieldInput: (ev, field) => {
          const value = ev.target.value;
          const wasFieldValidated = field in this.state.formErrors;
          this.updateFormValues({[field]: value});
          if (wasFieldValidated) {
            // if the field has been filled, we want to validate on input event
            // so we give user immediate feedback when they've gone from invalid -> valid input or vise versa
            this.validateFormFields([field]);
          }
          // remove already-invited-error after user changes their email address
          if (field === `email` && this.state.formErrors.alreadyInvitedError) {
            this.updateFormErrors({alreadyInvitedError: ``});
          }
        },
        handleDepartmentChange: (item) => {
          if (item?.label) {
            this.updateFormValues({
              department: item.label,
            });
          }
          this.validateFormFields([`department`]);
        },
        handleMauChange: (item) => {
          if (item?.label) {
            this.updateFormValues({
              numberOfMaus: item.label,
            });
          }
          this.validateFormFields([`numberOfMaus`]);
        },
        handleCompanySizeChange: (item) => {
          if (item?.label) {
            this.updateFormValues({numberOfEmployees: item.label});
          }
          this.validateFormFields([`numberOfEmployees`]);
        },
        handleTermsOfUseCheckboxClick: () => {
          const agreedToTermsOfUse = !this.state.formValues.agreedToTermsOfUse;
          this.updateFormValues({agreedToTermsOfUse});
          this.validateFormFields([`agreedToTermsOfUse`]);
        },
        sendRegistrationMagicLink: () => this.sendRegistrationMagicLink(),
      },
    };
  }

  async sendMagicLink(signupOptions) {
    try {
      const response = await apiClient.post(`/send_magic_link/`, objToFormData(signupOptions));
      if (response[`redirect_url`]) {
        window.location = response[`redirect_url`];
        this.update({
          notification: `Redirecting to SSO to complete signup`,
        });
      } else if (this.state.step === SignUpStep.ConfirmMagicLink) {
        // We are resending the link - show a toast
        this.update({
          notification: `We sent you another link. Check your inbox to complete registration.`,
        });
      } else {
        this.update({
          step: SignUpStep.ConfirmMagicLink,
        });
      }
    } catch (err) {
      this.update({
        error: err.message,
      });
    } finally {
      this.update({waitingForResponse: false});
    }
  }

  async sendRegistrationMagicLink() {
    if (this.state.waitingForResponse) {
      // We need to perform this check because the button is still clickable while disabled=true
      return;
    }
    this.update({waitingForResponse: true});
    const {creditStartup, incubator, guestId}: Partial<SignupOptions> = this.getExtraSignupOptions();
    const signupData = {
      email: this.state.formValues.email,
      isRegistration: true,
      creditStartup,
      incubator,
      next: this.attr(`next`),
      guestId,
    };
    await this.sendMagicLink(signupData);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async createSignupAccount() {
    if (this.state.waitingForResponse) {
      // We need to perform this check because the button is still clickable while disabled=true
      return;
    }
    this.update({waitingForResponse: true});
    let distinctId = null;
    let extras = {};
    if (mixpanel) {
      distinctId = mixpanel.get_distinct_id ? mixpanel.get_distinct_id() : null;
    }
    // Marketing (Salesforce) related data
    extras = {
      /* eslint-disable @typescript-eslint/naming-convention */
      Last_Touch_Medium__c: getCookie(`mixpanel_utm_medium`),
      Last_Touch_Source__c: getCookie(`mixpanel_utm_source`),
      Last_Touch_Campaign__c: getCookie(`mixpanel_utm_campaign`),
      Last_Touch_Content__c: getCookie(`mixpanel_utm_content`),
      Last_Touch_Keyword__c: getCookie(`mixpanel_utm_term`),
      Last_Touch_Webpage__c: getCookie(`mp_Last_Touch_Webpage__c`),
      Last_Touch_Test_Variant__c: getCookie(`mp_Last_Touch_Test_Variant__c`),
      Last_Touch_Test_ID__c: getCookie(`mp_Last_Touch_Test_ID__c`),
      Last_Touch_Ad_Variant__c: getCookie(`mp_Last_Touch_Ad_Variant__c`),
      /* eslint-enable */
    };
    const {formValues} = this.state;
    const {creditStartup, incubator, inviter}: Partial<SignupOptions> = this.getExtraSignupOptions();
    const signupOptions: SignupOptions = {
      name: formValues.name,
      email: formValues.email,
      department: formValues.department,
      password: formValues.password,
      socialCredential: formValues.socialCredential,
      company: formValues.company,
      phone: formValues.phone,
      distinctId,
      extras,
      isInvited: this.isInvited,
      inviter: inviter || ``,
      url: window.location.pathname,
      incubator: incubator || ``,
      creditStartup: !!creditStartup,
    };

    // backend takes the indices instead of the string value as a 1-indexed array for these
    if (formValues.numberOfEmployees) {
      signupOptions.numberOfEmployees = COMPANY_SIZE_OPTIONS.indexOf(formValues.numberOfEmployees) + 1;
    }

    if (formValues.numberOfMaus) {
      signupOptions.numberOfMaus = MAU_SIZE_OPTIONS.indexOf(formValues.numberOfMaus) + 1;
    }

    if (this.attr(`next`)) {
      signupOptions.next = this.attr(`next`);
    }

    if (this.attr(`promo-slug`)) {
      signupOptions.promoSlug = this.attr(`promo-slug`);
    }

    if (this.attr(`is-gdpr`)) {
      signupOptions.agreedToEmailPromo = this.state.formValues.agreedToEmailPromo;
    }

    // get lead id from our ShareASale affiliate program
    const sasCookie = getCookie(`sas_m_awin`);
    if (sasCookie) {
      signupOptions.sscid = JSON.parse(sasCookie).clickId;
    }

    registerProperties({[TrackProperties.Department]: formValues.department});
    if (this.isInvited && !formValues.socialCredential) {
      await this.sendMagicLink({...signupOptions, isRegistration: true});
    } else {
      const response = await api.signup(signupOptions);
      this.update({waitingForResponse: false});
      if (response.statusText === `success`) {
        window.location.href = response.redirectUrl;
      } else {
        // error signing up
        // clean up error message from backend and feed them into state
        const formErrorsUpdate: FormErrors = {};
        const dataFieldMap = {
          password1: FieldKey.Password,
          already_invited_error: ErrorKey.AlreadyInvitedError,
        };
        const errorFormattingMap = {
          password: (errors) => `Your password ${compoundSentence(errors)}.`,
        };
        Object.entries(response.errors).forEach(([field, errors]) => {
          const formfield = dataFieldMap[field] ? dataFieldMap[field] : field;
          const errorMessages = errors.map((reason) => reason.message);
          formErrorsUpdate[formfield] = errorFormattingMap[formfield]
            ? errorFormattingMap[formfield](errorMessages)
            : errorMessages.join(` `);

          if (formfield === ErrorKey.AlreadyInvitedError) {
            this.dispatchEvent(new CustomEvent(`alreadyInvitedError`, {detail: {error: formErrorsUpdate[formfield]}}));
          }
        });

        const firstErrorField = Object.keys(formErrorsUpdate)[0];

        const step = this.getStepFor(firstErrorField);

        this.update({formErrors: formErrorsUpdate, step});
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const queryParams = queryString.parse(window.location.search);
    const errType = queryParams.err_type;
    if (errType) {
      this.update({
        error: this.getErrorMessageByType(errType),
      });
    }
  }

  getErrorMessageByType(errType: ErrorTypes) {
    switch (errType) {
      case ErrorTypes.HEAD:
        return `Invalid request type. Try pasting the URL from the validation email directly into the browser.`;
      case ErrorTypes.EXPIRED_NONCE:
        return `Your login link has expired. Generate a new login email to try again.`;
      default:
        return `Something went wrong`;
    }
  }

  validateFormFields(fields: Array<keyof FormValues>): boolean {
    let isValid = true;
    const formErrors = {};
    fields.forEach((field) => {
      const validationError = validationRules[field].map((rule) => rule(field, this.state.formValues[field])).join(``);
      if (validationError !== ``) {
        isValid = false;
      }
      formErrors[field] = validationError;
    });
    this.updateFormErrors(formErrors);

    return isValid;
  }

  updateFormValues(update: Partial<FormValues>) {
    this.update({formValues: {...this.state.formValues, ...update}});
  }

  updateFormErrors(update: Partial<FormErrors>) {
    this.update({formErrors: {...this.state.formErrors, ...update}});
  }

  set defaultFormValues(defaultFormValues) {
    this.update({
      formValues: defaultFormValues,
    });
  }

  getExtraSignupOptions(): Partial<SignupOptions> {
    return {};
  }
  handleContinue?(ev: CustomEvent);
  handleBack?();
  abstract isInvited: boolean;
  abstract getStepFor(field: string): number;
  abstract getValidationStatus(field: string): ValidationState;
  abstract handleGoogleCredentialResponse(credential);
  abstract handleSignup(ev: CustomEvent);
}
